// 9fbef606107a605d69c0edbcd8029e5d
import { checkCacheValid, generateCacheTTL } from 'redux-cache';
import { select } from 'redux-saga/effects';
import { Map } from 'immutable';
import { getReducerKeyByAction } from './redux.util';
import { DEFAULT_REDUX_TTL_TIME } from '../config/site.config';
import { getLocalStorage, setLocalStorage } from './localStorageManagement';
import { isNewBuildDetected } from './buildNumber.util';

/*
   Redux cache is a library used to prevent api calls when correct data exists in redux already.
   It has a TTL based cache validation which defaults to 10 mins along with functionality of forcefully bursting a specific reducer cache.

   This utility over-rides the redux cache to provide as a wrapper for future.
   Idea is to use same cache utility for both redux and any other storage mechanism like local storage for api.
*/

/*
    validateCache is over-riding checkCacheValid to get State Object in itself and then validate the logic based
    arguments:
    action: - string - It is the action dispatched, which is needed to find the reducerKey (Reducer key - The key of the reducer to check whether the cache is still valid)
    args: object which might contain -
    cacheKey - string - default: DEFAULT_REDUCER_KEY. The cacheKey to be checking.
    accessStrategy - function - default: (state, reducerKey, cacheKey) => state[reducerKey][cacheKey]. Use this to overide the way in which the cacheKey is checked. This allows for greater configurability for applying the caching strategy to nested items in your reducer.
*/

export function* validateCache(action) {
  const state = yield select();
  const getState = () => state;
  const reducerKey = getReducerKeyByAction(action.type);
  if (!reducerKey) return false;
  return checkCacheValid(getState, reducerKey, {
    accessStrategy: (s, rKey, cacheKey) =>
      s[rKey] instanceof Map ? s[rKey].get(cacheKey) : s[rKey][cacheKey],
  });
}

/*
    validateReduxCache is higher order function which limits to run the saga method only when cache in redux expires.
    This helps to prevent unnecessary API calls in SPA while switching between pages. For more info refer to Redux-Cache.

    To forcefully execute the method, ignoreCache should be passed in payload of that action.
    arguments:
    sagaMethod - Function - Saga function which is used to request data from API - It needs to run only when data in redux expires
*/

function validateReduxCache(sagaMethod) {
  function* cachedSagaMethod(action) {
    const isCacheValid = yield validateCache(action);
    const ignoreCacheValidity = action.payload && action.payload.ignoreCache;

    if (isCacheValid && !ignoreCacheValidity) {
      return null;
    }
    return yield sagaMethod(action);
  }
  return cachedSagaMethod;
}

/*
    setCacheTTL is over-riding generateCacheTTL to override default ttl.
    arguments:
    ttl - time to live which is by default DEFAULT_REDUX_TTL_TIME
*/

function setCacheTTL(ttl = DEFAULT_REDUX_TTL_TIME) {
  return generateCacheTTL(ttl);
}

function calculateReduxCachedCartTTL(ttl, cacheConfigKey) {
  const { graphqlQueriesTimeStamps } = window || {};
  let cachedCartTTL = (graphqlQueriesTimeStamps && graphqlQueriesTimeStamps[cacheConfigKey]) || 0;

  if (cachedCartTTL > 0) {
    const diff = cachedCartTTL + ttl * 60 * 1000 - Date.now();
    cachedCartTTL = diff > 0 ? diff : 0;
  }
  return cachedCartTTL;
}

const APOLLO_CACHE = 'apollo-cache-persist';
const APOLLO_CACHE_TTL = 'GRAPHQL_CACHED_RESPONSE';

const deleteApolloPersistCache = () => {
  setLocalStorage({ key: APOLLO_CACHE, value: JSON.stringify({}) });
  setLocalStorage({ key: APOLLO_CACHE_TTL, value: JSON.stringify({}) });
};

const deleteCache = async (queries = []) => {
  const cachedData = getLocalStorage(APOLLO_CACHE);
  const cache = JSON.parse(cachedData);

  const cacheKeys = Object.keys(cache);
  const rootQueryKeys = cache.ROOT_QUERY && Object.keys(cache.ROOT_QUERY);
  queries.forEach((query) => {
    cacheKeys.forEach((key) => {
      if (key.includes(query)) {
        delete cache[key];
      }
    });
    rootQueryKeys.forEach((key) => {
      if (key.includes(query)) {
        delete cache.ROOT_QUERY[query];
      }
    });
    setLocalStorage({ key: APOLLO_CACHE, value: JSON.stringify(cache) });
  });
};

const deleteTtlCache = async (queries = []) => {
  const cachedData = getLocalStorage('GRAPHQL_CACHED_RESPONSE');

  const ttlCache = JSON.parse(cachedData);
  const cacheKeys = Object.keys(ttlCache);
  queries.forEach((query) => {
    cacheKeys.forEach((key) => {
      if (key.includes(query)) {
        delete ttlCache[key];
      }
    });
  });

  setLocalStorage({ key: 'GRAPHQL_CACHED_RESPONSE', value: JSON.stringify(ttlCache) });
  if (window) {
    window.graphqlQueriesTimeStamps = ttlCache;
  }
};
const persistCacheOperations = () => {
  if (isNewBuildDetected()) {
    deleteApolloPersistCache();
  }
};
const getCache = async (searchKey) => {
  const cachedData = getLocalStorage(APOLLO_CACHE);
  const cache = JSON.parse(cachedData);

  if (cache) {
    const cacheKeys = Object.keys(cache);
    const cachedKey = cacheKeys.find((key) => key === searchKey);
    if (cachedKey) {
      return cache[cachedKey];
    }
    const rootQueryKeys = cache.ROOT_QUERY && Object.keys(cache.ROOT_QUERY);
    const cachedRootKey = rootQueryKeys && rootQueryKeys.find((key) => key === searchKey);
    if (cachedRootKey) {
      return cache?.ROOT_QUERY[cachedRootKey];
    }
    return null;
  }
  return null;
};

export {
  setCacheTTL,
  validateReduxCache,
  calculateReduxCachedCartTTL,
  deleteCache,
  deleteTtlCache,
  getCache,
  persistCacheOperations,
};
export {
  invalidateCache as invalidateReduxCache,
  cacheEnhancer as cacheEnhancerMiddleware,
  DEFAULT_KEY as DEFAULT_REDUCER_KEY,
} from 'redux-cache';
